import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ChatTripPlannerMobileBlocker from './ChatTripPlannerMobileBlocker'
import { useParams } from 'react-router'
import { getFirstName, isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { useDeleteTrip, useRecentTrips } from 'tripPlanner/hooks/api/trip'
import { TripPlannerEventBusProvider } from './ChatTripPlannerPage/contexts/TripPlannerEventBus'
import { TripPlannerChatProvider } from './ChatTripPlannerPage/contexts/TripPlannerChatProvider'
import { useAnonymousChatClaiming } from './ChatTripPlannerPage/hooks/useAnonymousChatClaiming'
import { useChatHistory } from './ChatTripPlannerPage/hooks/useChatHistory'
import { NEW_CHAT_ROUTE_SEGMENT, useChatNavigation } from './ChatTripPlannerPage/hooks/useChatNavigation'
import { useChatTitleGeneration } from './ChatTripPlannerPage/hooks/useChatTitleGeneration'
import { usePromptManagement } from './ChatTripPlannerPage/hooks/usePromptManagement'
import ChatPageContent from './ChatTripPlannerPage/ChatPageContent'

function ChatTripPlannerPage() {
  const intl = useIntl()
  const deviceType = useAppSelector(state => state.system.device.deviceType)
  const isMobileOrTablet = deviceType === 'mobile' || deviceType === 'tablet'

  const isStaff = useAppSelector(isLEStaff)
  const canManagePrompts = isStaff
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const firstName = useAppSelector(getFirstName)
  const { latitude, longitude } = useContext(GeoContext)
  const userData = useMemo(() => ({
    firstName: firstName || undefined,
    lat: latitude,
    lng: longitude,
  }), [firstName, latitude, longitude])

  const [isDevMode, setIsDevMode] = useState(false)
  const [bookingSelection, setBookingSelection] = useState<{ orderIds: Array<string>; offerIds: Array<string> }>({ orderIds: [], offerIds: [] })
  const { chatId: routeChatIdParam } = useParams<{ chatId?: string }>()

  const {
    chatId, setChatId: syncChatId, setChatIdForStreaming,
    isNewChatRoute, isLandingRoute, existingRouteChatId, navigateToNewChatRoute,
  } = useChatNavigation({ routeChatId: routeChatIdParam })

  const chatKey = useMemo(
    () => (chatId !== undefined ? String(chatId) : NEW_CHAT_ROUTE_SEGMENT),
    [chatId],
  )

  const {
    chatHistory, recentChats, refetchChatList,
    deleteChatMutation, claimChatsMutation,
    updateChatLastActivityTimestamp, startChatTitleGeneration: startTitleGen,
  } = useChatHistory({ isLoggedIn })

  const { startChatTitleGeneration } = useChatTitleGeneration({ startTitleGen })

  const {
    selectedPromptKey, promptOptions, selectedPrompt,
    loadPrompts, handlePromptAdd, handlePromptSelect,
  } = usePromptManagement({ canManagePrompts })

  const { data: previousTrips, refetch: refetchTrips } = useRecentTrips()
  const { mutateAsync: deleteTripMutation } = useDeleteTrip({
    onSuccess: () => {
      refetchTrips()
    },
    onError: (error) => {
      console.error('[TripChat] Failed to delete trip', error)
      showSnackbar(intl.formatMessage({
        id: 'FtZ79RamZe2rLll_1NZF',
        defaultMessage: 'Unable to delete trip. Please try again.',
        description: 'Error snackbar message when trip deletion fails in the trip planner AI chat',
      }), 'critical')
    },
  })

  useAnonymousChatClaiming({
    isLoggedIn,
    claimChatsMutation,
    refetchChatList,
  })

  const handleDevModeToggle = useCallback(() => {
    setIsDevMode((prev) => !prev)
  }, [])

  if (isMobileOrTablet) {
    return <ChatTripPlannerMobileBlocker />
  }

  return (
    <TripPlannerEventBusProvider>
      <TripPlannerChatProvider
        chatId={chatId}
        setChatIdForStreaming={setChatIdForStreaming}
        prompt={selectedPrompt.promptId}
        isDevMode={isDevMode}
        isLoggedIn={isLoggedIn}
        orderIds={bookingSelection.orderIds}
        offerIds={bookingSelection.offerIds}
        refetchChatList={refetchChatList}
        startChatTitleGeneration={startChatTitleGeneration}
        updateChatLastActivityTimestamp={updateChatLastActivityTimestamp}
        userData={userData}
        chatKey={chatKey}
      >
        <ChatPageContent
          chatId={chatId}
          syncChatId={syncChatId}
          isNewChatRoute={isNewChatRoute}
          isLandingRoute={isLandingRoute}
          existingRouteChatId={existingRouteChatId}
          navigateToNewChatRoute={navigateToNewChatRoute}
          chatHistory={chatHistory}
          recentChats={recentChats}
          refetchChatList={refetchChatList}
          deleteChatMutation={deleteChatMutation}
          previousTrips={previousTrips}
          deleteTripMutation={deleteTripMutation}
          isLoggedIn={isLoggedIn}
          isStaff={isStaff}
          isDevMode={isDevMode}
          onDevModeToggle={handleDevModeToggle}
          canManagePrompts={canManagePrompts}
          promptOptions={promptOptions}
          selectedPromptKey={selectedPromptKey}
          handlePromptAdd={handlePromptAdd}
          handlePromptSelect={handlePromptSelect}
          loadPrompts={loadPrompts}
          setBookingSelection={setBookingSelection}
        />
      </TripPlannerChatProvider>
    </TripPlannerEventBusProvider>
  )
}

export default ChatTripPlannerPage
